import { InputLabel, Select, MenuItem, Checkbox, TextField } from "@mui/material";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";

import TextareaAutosize from "@mui/base/TextareaAutosize";
import { padding, styled } from "@mui/system";
import FormControl from "@mui/material/FormControl";

import React, { useState, useEffect, useRef } from "react";
import { CapitalPlanService } from "services/CapitalPlanService";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import {
  CapitalProject,
  setCapitalProjectsStore,
  getCapitalProjects,
} from "redux/slices/capitalProjectsSlice";
import {
  getCapitalPlanTableData,
  data,
  CapitalPlanTableData,
} from "redux/slices/capitalPlanTableSlice";
import { updateCapitalProject } from "redux/slices/updateDataSlice";
import { currency, numberWithComma } from "../../util/valueFormatter";
import { FroalaRichText } from "components/util/FroalaRichtext";
import { AutosaveDelay, CommentKey, CommentSectionName } from "constant";
import {
  updateDapCommentData,
  DapComment,
} from "redux/slices/updateDapCommentSlice";
import { saveDapCommentData } from "redux/slices/saveCommentSlice";
import { clearDocumentComments } from "components/util/storeManager";
import { postApiCall } from "services/apiHelper";
import { baseUrl } from "config/config";
import { GenericToast } from "components/util/ToastMessage";

interface RowWiseData {
  [key: string]: Array<string | number | number | number | number | number>;
}
export const StyledTextarea = styled(TextareaAutosize)(
  () => `
  font-family: sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  padding: 12px;
  border-radius: 12px 12px 0 12px;
`
);
const label = { inputProps: { "aria-label": "Checkbox demo" } };
export const CapitalPlanEditForm = () => {
  const rowWiseData: RowWiseData = {};
  const dispatch = useAppDispatch();


  const assetPlanId = useAppSelector(
    (state) => state.getAssetPlanId.assetPlanId
  );

  const budgetYear = Number(
    useAppSelector((state) => state.summary.dapData?.budgetYear)
  );

  const capitalPlanTableData: CapitalPlanTableData[] = useAppSelector(
    (state) => state.capitalPlanTable.capitalPlan
  );
  const capitalProjectData: CapitalProject[] = useAppSelector(
    (state) => state.capitalProjects.data
  );

  const isNoData: boolean = useAppSelector(
    (state) => state.capitalProjects.isNoData
  );

  const dapData: any = useAppSelector((state) => state.summary.dapData);
  const isUS = dapData?.internationalInd !== "Y";
  const [yearRangeSelected, setYearRangeSelected] = useState<number>(1);
  const [dropdownItems, setDropdownItems] = useState<any[]>([]);
  const [glAccounts, setGlaccounts] = useState<any>();
  const capitalPlanService = new CapitalPlanService();
  const columns: string[] = [
    "year",
    "biCapital",
    "leasingCostCommercial",
    "turnCostResidential",
    "totalCapital",
  ];
  const CPColumnNameArray: string[] = [
    "BI Capital",
    "Leasing Cost (Commercial)",
    "Turn Cost (Residential)",
    "Total Capital",
    `Projected NOI ${budgetYear}`,
  ];

  const columnNames: string[] = [
    //change the year from the elements of array with year which is comming from redux store.
    `${budgetYear} Capital`,
    `% of ${budgetYear} NOI`,
    `${budgetYear + 1} Capital`,
    `${budgetYear + 2} Capital`,
    `${budgetYear + 3} Capital`,
    `${budgetYear + 4} Capital`,
  ];
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({ 'Turn Costs': true });



  useEffect(() => {
    dispatch(getCapitalPlanTableData({ assetPlanID: assetPlanId }));
    dispatch(getCapitalProjects({ assetPlanId: assetPlanId }));
    capitalPlanService.getDropdownItems().then((data) => {
      setDropdownItems(data);
    });
    getGlAccountData('Turn Costs')
  }, [dispatch, assetPlanId, yearRangeSelected]);

  const getGlAccountData = async (grp_des: string) => {
    const request = {
      investment_Id: dapData?.investmentId,
      budget_Year: dapData?.budgetYear,
      group_Desc: grp_des
    }
    const response = await postApiCall(`${baseUrl}AssetPlan/GetCapitalPlanGL_Accounts`, request);
    setGlaccounts(prev => ({
      ...prev,
      [grp_des]: response
    }))

  }

  const columnDataToRowData = () => {
    var projectedNoi: number = capitalPlanTableData[0]?.projectedNOI;

    capitalPlanTableData?.forEach((obj) => {
      Object.keys(obj).forEach((key) => {
        if (!rowWiseData[key]) {
          rowWiseData[key] = [];
        }
        rowWiseData[key].push(obj[key]);
        // rowWiseData[key][0] = currency(rowWiseData[key][0])
      });
    });

    columns.forEach((name) => {
      var num = rowWiseData[name]?.at(0);
      if (num) {
        rowWiseData[name]?.splice(1, 0, (Number(num) / projectedNoi) * 100);
      }
    });

    capitalPlanTableData?.forEach((obj) => {
      Object.keys(obj).forEach((key) => {
        rowWiseData[key][0] = currency(rowWiseData[key][0]);
        rowWiseData[key][1] = numberWithComma(rowWiseData[key][1]);
      });
    });
  };
  columnDataToRowData();

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    return dateString.split('T')[0];
  };
  const updateProjectPriority = (e: any, id: number) => {
    // make copy of local state obj
    let capitalProjectStateCopy: any = [...capitalProjectData];
    const itemIndex = capitalProjectStateCopy.findIndex((c) => c.id == id);

    // update copy with new changes
    let filteredProject = { ...capitalProjectStateCopy[itemIndex] };
    filteredProject.projectPriority = e.target.value;
    capitalProjectStateCopy[itemIndex] = filteredProject;
    dispatch(setCapitalProjectsStore(capitalProjectStateCopy));
    sendProjectToUpdateDataSlice(filteredProject);
  };

  const updateEsgImpact = (e: any, id: number) => {
    let capitalProjectStateCopy: any = [...capitalProjectData];
    const itemIndex = capitalProjectStateCopy.findIndex((c) => c.id == id);
    let filteredProject = { ...capitalProjectStateCopy[itemIndex] };
    filteredProject.esgImpact = e.target.checked;
    capitalProjectStateCopy[itemIndex] = filteredProject;
    //setCapitalProjectData(capitalProjectStateCopy);
    dispatch(setCapitalProjectsStore(capitalProjectStateCopy));
    // send updated project to store
    sendProjectToUpdateDataSlice(filteredProject);
  };

  const updateComments = (e: any, id: number) => {
    let capitalProjectStateCopy: any = [...capitalProjectData];
    const itemIndex = capitalProjectStateCopy.findIndex((c) => c.id == id);
    let filteredProject = { ...capitalProjectStateCopy[itemIndex] };
    filteredProject.comment = e.target.value;
    capitalProjectStateCopy[itemIndex] = filteredProject;
    //setCapitalProjectData(capitalProjectStateCopy);
    dispatch(setCapitalProjectsStore(capitalProjectStateCopy));
    // send updated project to store
    sendProjectToUpdateDataSlice(filteredProject);
  };

  const updateProjectStart = (date: any, id: number) => {
    let capitalProjectStateCopy: any = [...capitalProjectData];
    const itemIndex = capitalProjectStateCopy.findIndex((c) => c.id == id);
    let filteredProject = { ...capitalProjectStateCopy[itemIndex] };
    filteredProject.projectStartDate = date;
    capitalProjectStateCopy[itemIndex] = filteredProject;
    dispatch(setCapitalProjectsStore(capitalProjectStateCopy));
    sendProjectToUpdateDataSlice(filteredProject);
  };

  const updateProjectEnd = (date: any, id: number) => {
    let capitalProjectStateCopy: any = [...capitalProjectData];
    const itemIndex = capitalProjectStateCopy.findIndex((c) => c.id == id);
    let filteredProject = { ...capitalProjectStateCopy[itemIndex] };
    filteredProject.projectEndDate = date;
    capitalProjectStateCopy[itemIndex] = filteredProject;
    dispatch(setCapitalProjectsStore(capitalProjectStateCopy));
    sendProjectToUpdateDataSlice(filteredProject);
  };

  const updateFundingMethod = (e: any, id: number) => {
    let capitalProjectStateCopy: any = [...capitalProjectData];
    const itemIndex = capitalProjectStateCopy.findIndex((c) => c.id == id);
    let filteredProject = { ...capitalProjectStateCopy[itemIndex] };
    filteredProject.methodOfFunding = e.target.value;
    capitalProjectStateCopy[itemIndex] = filteredProject;
    dispatch(setCapitalProjectsStore(capitalProjectStateCopy));
    sendProjectToUpdateDataSlice(filteredProject);
  };

  const updateAdditiveCost = (e: any, id: number) => {
    let capitalProjectStateCopy: any = [...capitalProjectData];
    const itemIndex = capitalProjectStateCopy.findIndex((c) => c.id == id);
    let filteredProject = { ...capitalProjectStateCopy[itemIndex] };
    filteredProject.additiveToBookCost = Number(e.target.value);
    capitalProjectStateCopy[itemIndex] = filteredProject;
    dispatch(setCapitalProjectsStore(capitalProjectStateCopy));
    sendProjectToUpdateDataSlice(filteredProject);
  };

  const sendProjectToUpdateDataSlice = (capitalProject: any) => {
    dispatch(
      updateCapitalProject({
        projectPriority: capitalProject.projectPriority,
        esgImpact: capitalProject.esgImpact,
        comment: capitalProject.comment,
        projectStartDate: capitalProject.projectStartDate,
        projectEndDate: capitalProject.projectEndDate,
        methodOfFunding: capitalProject.methodOfFunding,
        additiveToBookCost: capitalProject.additiveToBookCost,
        id: capitalProject.id,
      })
    );
  };
  // Add a function to handle row expansion
  const toggleRowExpansion = (rowKey: string) => {
    getGlAccountData(rowKey)
    setExpandedRows(prev => ({
      ...prev,
      [rowKey]: !prev[rowKey]
    }));
  };



  const comments = useAppSelector((state) => state.dapComments?.data).find(
    (x) => x.commentKey.toLowerCase() === CommentKey.CAPITALPLAN.toLowerCase()
  );

  const handleCommentsChange = (newValue: string) => {
    const updatedcomment: DapComment = {
      id: comments ? comments.id : 0,
      commentKey: comments ? comments.commentKey : CommentKey.CAPITALPLAN,
      commentText: newValue || "",
      sectionName: CommentSectionName.CAPITALPLAN,
      assetPlanId: assetPlanId,
    };
    dispatch(updateDapCommentData(updatedcomment));
    dispatch(saveDapCommentData(updatedcomment));

  };

  const renderTableData = () => {
    if (capitalProjectData.length !== 0) {
      return capitalProjectData?.map((val, i) => (
        <>
          <TableRow sx={{ textAlign: "center" }} key={i}>
            <TableCell sx={{ width: "10%" }}>
              <FormControl fullWidth>
                <InputLabel>Select</InputLabel>
                <Select
                  sx={{
                    height: '32px',
                    '& .MuiSelect-select': {
                      margin: "0px",
                      fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                      fontSize: "0.875rem",
                      color: "rgba(0, 0, 0, 0.6)",
                      padding: '4px 14px',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(0, 0, 0, 0.87)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                    }
                  }}
                  value={val.projectPriority ? val.projectPriority : " "}
                  label="Self"
                  onChange={(e) => updateProjectPriority(e, val.id)}
                >
                  {dropdownItems.map((element) => (
                    <MenuItem value={element} key={element}>
                      {element}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </TableCell>
            <TableCell sx={{
              height: '32px',
              '& .MuiSelect-select': {
                margin: "0px",
                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                fontSize: "0.875rem",
                color: "rgba(0, 0, 0, 0.6)",
                padding: '4px 14px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              }
            }}>
              <Checkbox
                checked={val.esgImpact}
                onChange={(e) => updateEsgImpact(e, val.id)}
              />
            </TableCell>
            <TableCell sx={{
              height: '32px',
              '& .MuiSelect-select': {
                margin: "0px",
                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                fontSize: "0.875rem",
                color: "rgba(0, 0, 0, 0.6)",
                padding: '4px 14px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              }
            }}>{val.projectId}</TableCell>
            <TableCell sx={{
              height: '32px',
              '& .MuiSelect-select': {
                margin: "0px",
                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                fontSize: "0.875rem",
                color: "rgba(0, 0, 0, 0.6)",
                padding: '4px 14px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              }
            }}>
              {currency(val.currentBudget)}
            </TableCell>
            <TableCell sx={{
              height: '32px',
              '& .MuiSelect-select': {
                margin: "0px",
                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                fontSize: "0.875rem",
                color: "rgba(0, 0, 0, 0.6)",
                padding: '4px 14px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              }
            }}>
              {val.projectDescription}
            </TableCell>
            <TableCell sx={{
              height: '32px',
              '& .MuiSelect-select': {
                margin: "0px",
                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                fontSize: "0.875rem",
                color: "rgba(0, 0, 0, 0.6)",
                padding: '4px 14px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.23)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(0, 0, 0, 0.87)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#1976d2',
              }
            }}>
              <TextField
                type="date"
                fullWidth
                size="small"
                InputProps={{
                  style: {
                    margin: "0px",
                    fontFamily:
                      "Roboto, Helvetica, Arial, sans-serif",
                    fontSize: "0.875rem",
                    color: "rgba(0, 0, 0, 0.6)",
                  },
                }}
                InputLabelProps={{ shrink: true }}
                value={formatDate(val.projectStartDate) || ""}
                onChange={(e) => {
                  updateProjectStart(e.target.value, val.id)
                }}
              />
            </TableCell>
            <TableCell sx={{ width: "8%" }}>
              <TextField
                type="date"
                fullWidth
                size="small"
                InputProps={{
                  style: {
                    margin: "0px",
                    fontFamily:
                      "Roboto, Helvetica, Arial, sans-serif",
                    fontSize: "0.875rem",
                    color: "rgba(0, 0, 0, 0.6)",
                  },
                }}
                InputLabelProps={{ shrink: true }}
                value={formatDate(val.projectEndDate) || ""}
                onChange={(e) => updateProjectEnd(e.target.value, val.id)}
              />
            </TableCell>
            {!isUS && (
              <TableCell sx={{ width: "8%" }}>
                <TextField
                  fullWidth
                  size="small"
                  InputProps={{
                    style: {
                      margin: "0px",
                      fontFamily:
                        "Roboto, Helvetica, Arial, sans-serif",
                      fontSize: "0.875rem",
                      color: "rgba(0, 0, 0, 0.6)",
                    },
                  }}
                  value={val.methodOfFunding || ""}
                  onChange={(e) => updateFundingMethod(e, val.id)}
                />
              </TableCell>
            )}
            {!isUS && (
              <TableCell sx={{ width: "8%" }}>
                <TextField
                  type="number"
                  fullWidth
                  size="small"
                  InputProps={{
                    style: {
                      margin: "0px",
                      fontFamily:
                        "Roboto, Helvetica, Arial, sans-serif",
                      fontSize: "0.875rem",
                      color: "rgba(0, 0, 0, 0.6)",
                    },
                  }}
                  value={val.additiveToBookCost || ""}
                  onChange={(e) => updateAdditiveCost(e, val.id)}
                />
              </TableCell>
            )}

            <TableCell sx={{ width: "20%" }}>
              <StyledTextarea
                sx={{ width: "100%" }}
                minRows={2}
                defaultValue={val.comment}
                onChange={(e) => updateComments(e, val.id)}
              />
            </TableCell>
          </TableRow>
        </>
      ));
    }
  };

  return (
    <div>
      <div className="tab-heading text-center">
        <h3 className="heading">Detailed Building Capital Plan</h3>
      </div>

      <div className="responsive-table-wrapper">
        <Table id="customers" className="responsive-table">
          <TableRow>
            <th>Project Priority</th>
            <th style={{ textAlign: "center" }}>{budgetYear <= 2024 ? `ESG Impact ` : `Contributes to SSBB feature`}</th>
            <th>Project ID</th>
            <th style={{ textAlign: "right" }}>{budgetYear} Budget</th>
            <th>Project Description</th>
            <th>Project Start</th>
            <th>Project End Date</th>
            {!isUS && <th>Method of Funding</th>}
            {!isUS && <th>Additive to book cost</th>}
            <th>Comments</th>
          </TableRow>
          <TableBody>{renderTableData()}</TableBody>
          {isNoData && (
            <TableRow>
              <TableCell colSpan={10} sx={{ textAlign: "center !important" }}>
                No rows
              </TableCell>{" "}
            </TableRow>
          )}
        </Table>
      </div>

      <div className="tab-heading text-center">
        <h3 className="heading">Capital Plan</h3>
      </div>
      <div>
        <table id="customers">
          <tr>
            <th></th>
            {columnNames
              ?.filter((_, i) => i < 1 + yearRangeSelected)
              .map((ele, index) => (
                <th style={{ textAlign: "right" }} key={index}>
                  {ele}
                </th>
              ))}
          </tr>
          <tbody>
            <tr>
              <th
                className="grey-bg"
                style={{ cursor: 'pointer', width: '700px' }}
                onClick={() => toggleRowExpansion('BI Capital')}
              >
                {expandedRows['BI Capital'] ? '▼' : '▶'} BI Capital
              </th>
              {rowWiseData.biCapital
                ?.filter((_, i) => i < 1 + yearRangeSelected)
                .map((ele, index) => (
                  <td style={{ textAlign: "right" }} key={index}>
                    {ele}
                  </td>
                ))}
            </tr>
            {expandedRows['BI Capital'] && glAccounts?.['BI Capital'] && (
              <tr>
                <td colSpan={2} style={{ padding: '0px' }}>
                  <table id="GLAccount" style={{ width: '100%' }}>
                    {glAccounts['BI Capital']?.map((account, index) => (
                      <tr key={index}>
                        <td style={{ width: '700px' }}>{account.account_Desc}</td>
                        <td style={{ textAlign: "right" }}>{currency(account.year1_budget)}</td>
                      </tr>
                    ))}
                  </table>
                </td>
              </tr>
            )}
            <tr>
              <th
                className="grey-bg"
                style={{ cursor: 'pointer', width: '700px' }}
                onClick={() => toggleRowExpansion('Leasing Cost')}>
                {expandedRows['Leasing Cost'] ? '▼' : '▶'}Leasing Cost (Commercial)</th>
              {rowWiseData.leasingCostCommercial
                ?.filter((_, i) => i < 1 + yearRangeSelected)
                .map((ele, index) => (
                  <td style={{ textAlign: "right" }} key={index}>
                    {ele}
                  </td>
                ))}
            </tr>
            {expandedRows['Leasing Cost'] && glAccounts?.['Leasing Cost'] && (
              <tr>
                <td colSpan={2} style={{ padding: '0px' }}>
                  <table id="GLAccount" style={{ width: '100%' }}>
                    {glAccounts['Leasing Cost']?.map((account, index) => (
                      <tr key={index}>
                        <td style={{ width: '700px' }}>{account.account_Desc}</td>
                        <td style={{ textAlign: "right" }}>{currency(account.year1_budget)}</td>
                      </tr>
                    ))}
                  </table>
                </td>
              </tr>
            )}
            <tr>
              <th className="grey-bg"
                style={{ cursor: 'pointer', width: '700px' }}
                onClick={() => toggleRowExpansion('Turn Costs')}>
                {expandedRows['Turn Costs'] ? '▼' : '▶'}Turn Cost (Residential)</th>
              {rowWiseData.turnCostResidential
                ?.filter((_, i) => i < 1 + yearRangeSelected)
                .map((ele, index) => (
                  <td style={{ textAlign: "right" }} key={index}>
                    {ele}
                  </td>
                ))}
            </tr>
            {expandedRows['Turn Costs'] && rowWiseData.turnCostResidential &&(
              <tr>
                <td colSpan={2} style={{ padding: '0px' }}>
                  <table id="GLAccount" style={{ width: '100%' }}>
                    <tr>
                      <td colSpan={2} style={{ padding: '0px' }}>
                        <table id="GLAccount" style={{ width: '100%' }}>
                          <tr >
                            <td style={{ width: '700px' }}>Unit Rehab</td>
                            <td style={{ textAlign: "right" }}>{currency((Object.values(glAccounts?.['Turn Costs'] || {}).find((account: any) =>
                              account.account_Desc?.toLowerCase() === 'unit rehab'
                            ) as any)?.year1_budget) || '$0'}
                            </td>
                          </tr>
                          <tr >
                            <td style={{ width: '700px' }}>Unit Turn Cost</td>
                            <td style={{ textAlign: "right" }}>{currency((Object.values(glAccounts?.['Turn Costs'] || {}).find((account: any) =>
                              account.account_Desc?.toLowerCase() === 'unit turn cost'
                            ) as any)?.year1_budget) || '$0'}
                            </td>
                          </tr>
                        </table>
                      </td>
                    </tr>
                  </table>
                </td>
              </tr>
            )}
            <tr>
              <th className="grey-bg">Total Capital</th>
              {rowWiseData.totalCapital
                ?.filter((_, i) => i < 1 + yearRangeSelected)
                .map((ele, index) => (
                  <td style={{ textAlign: "right" }} key={index}>
                    {ele}
                  </td>
                ))}
            </tr>
            <tr>
              <th className="grey-bg">Projected NOI {budgetYear}</th>
              {rowWiseData.projectedNOI
                ?.filter((_, i) => i < 1 + yearRangeSelected)
                .map((ele, index) => (
                  <td style={{ textAlign: "right" }} key={index}>
                    {ele}
                  </td>
                ))}
            </tr>
          </tbody>
        </table>
      </div>

      <form className="m-20 lease-form">
        <fieldset>
          <legend className="lease-title">Comments</legend>

          <FroalaRichText
            model={comments?.commentText || ""}
            handlesModelChange={handleCommentsChange}
          />
        </fieldset>
      </form>
    </div>
  );
};